import React, { useState, useEffect } from 'react';
import { X, Mail, Plus, Trash2 } from 'lucide-react';
import { dashboardAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// Extract domain from URL (e.g. https://www.example.com/path -> example.com)
const getDomainFromUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  let u = url.trim();
  if (!u.startsWith('http')) u = 'https://' + u;
  try {
    const host = new URL(u).hostname;
    return host.replace(/^www\./, '');
  } catch {
    return '';
  }
};

// First name from full name (first word)
const getFirstName = (name) => {
  if (!name || typeof name !== 'string') return '';
  return name.trim().split(/\s+/)[0] || '';
};

const FollowUpEmailsPanel = ({ isOpen, onClose, lead, formData }) => {
  const { user } = useAuth();
  const [emailList, setEmailList] = useState([]); // [{ email: string }]
  const [template1, setTemplate1] = useState('Hi {{first_name}},\n\nFollowing up on our recent conversation regarding {{company_name}}. We would like to schedule a call at your convenience.\n\nBest regards');
  const [template2, setTemplate2] = useState('Hi {{first_name}},\n\nThis is a second follow-up regarding {{company_name}}. Please let us know if you have any questions.\n\nBest regards');
  const [template3, setTemplate3] = useState('Hi {{first_name}},\n\nFinal follow-up regarding {{company_name}}. We are here when you are ready to move forward.\n\nBest regards');
  const [subjectPrefix, setSubjectPrefix] = useState('Follow-up - {{company_name}}');
  const [sending, setSending] = useState(null); // 1 | 2 | 3
  const [message, setMessage] = useState({ type: '', text: '' });

  const companyName = (lead?.company_name || formData?.company_name || '').trim();
  const companyUrl = (lead?.company_url || formData?.company_url || '').trim();
  const domain = getDomainFromUrl(companyUrl);

  // Scrape: build list from POC emails + suggest firstname@domain
  const handleScrapeFromLead = () => {
    const seen = new Set();
    const list = [];
    const add = (email, _name) => {
      const e = (email && typeof email === 'string' ? email.trim() : '').toLowerCase();
      if (e && e.includes('@') && !seen.has(e)) {
        seen.add(e);
        list.push({ email: e });
      }
    };
    const suggest = (name) => {
      const first = getFirstName(name);
      if (!first || !domain) return;
      const suggested = `${first.toLowerCase().replace(/[^a-z0-9]/g, '')}@${domain}`;
      if (!seen.has(suggested)) {
        seen.add(suggested);
        list.push({ email: suggested });
      }
    };
    add(formData?.email || lead?.email);
    add(formData?.poc2_email || lead?.poc2_email);
    add(formData?.poc3_email || lead?.poc3_email);
    add(formData?.poc4_email || lead?.poc4_email);
    suggest(formData?.poc_name || lead?.poc_name);
    suggest(formData?.poc2_name || lead?.poc2_name);
    suggest(formData?.poc3_name || lead?.poc3_name);
    suggest(formData?.poc4_name || lead?.poc4_name);
    setEmailList(list.length ? list : [{ email: '' }]);
    setMessage({ type: 'success', text: list.length ? `Added ${list.length} email(s) from lead.` : 'No emails to add. You can add manually.' });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  useEffect(() => {
    if (isOpen && emailList.length === 0) {
      setEmailList([{ email: '' }]);
    }
  }, [isOpen]);

  const updateEmail = (index, value) => {
    setEmailList(prev => {
      const next = [...prev];
      next[index] = { ...next[index], email: value };
      return next;
    });
  };

  const removeEmail = (index) => {
    setEmailList(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : [{ email: '' }]);
  };

  const addEmail = () => {
    setEmailList(prev => [...prev, { email: '' }]);
  };

  const getValidEmails = () => {
    return emailList.map(i => (i && i.email ? i.email.trim() : '')).filter(Boolean);
  };

  const sendFollowUp = async (number) => {
    const to = getValidEmails();
    if (to.length === 0) {
      setMessage({ type: 'error', text: 'Add at least one valid email.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }
    const templates = { 1: template1, 2: template2, 3: template3 };
    const body = templates[number];
    const subject = subjectPrefix.replace(/\{\{\s*company[\s_-]*name\s*\}\}/gi, companyName || '') + ` (Follow-up ${number})`;
    let htmlBody = body.replace(/\{\{\s*company[\s_-]*name\s*\}\}/gi, companyName || '');
    htmlBody = htmlBody.replace(/\{\{\s*first[\s_-]*name\s*\}\}/gi, 'there');
    htmlBody = htmlBody.split('\n').map(p => `<p>${p}</p>`).join('');
    setSending(number);
    setMessage({ type: '', text: '' });
    try {
      const effectiveSlot = (user?.email_sender_slot >= 1 && user?.email_sender_slot <= 4) ? user.email_sender_slot : undefined;
      const result = await dashboardAPI.sendFollowUpEmails({
        to,
        subject,
        html: `<!DOCTYPE html><html><body>${htmlBody}</body></html>`,
        senderSlot: effectiveSlot,
        userId: user?.id,
        userEmail: user?.email,
        // Without leadId the backend cannot tie this to the earlier emails, so
        // Follow-up 2 would open a fresh thread instead of replying to 1.
        leadId: lead?.id,
        followUpNumber: number
      });
      if (result.success) {
        setMessage({ type: 'success', text: `Follow-up ${number} sent to ${result.sent} recipient(s).` });
      } else {
        setMessage({ type: 'error', text: result.error || (result.failed ? `${result.failed} failed` : 'Send failed') });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || err.message || 'Failed to send' });
    } finally {
      setSending(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 4000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative ml-auto w-full max-w-lg bg-white shadow-xl flex flex-col h-full overflow-hidden">
        <div className="flex-shrink-0 border-b border-gray-200 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="w-6 h-6 text-[#679632]" />
            <h2 className="text-lg font-bold">Follow-up Emails</h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700 rounded-lg">
            <X size={22} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {companyName && (
            <p className="text-sm text-gray-600">
              <span className="font-medium">Company:</span> {companyName}
            </p>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">POC Emails</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleScrapeFromLead}
                  className="text-xs px-2 py-1.5 bg-[#679632] text-white rounded-lg hover:bg-[#587f2b]"
                >
                  Scrape from lead
                </button>
                <button type="button" onClick={addEmail} className="text-xs px-2 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1">
                  <Plus size={14} /> Add
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {emailList.map((item, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <input
                    type="email"
                    value={item.email || ''}
                    onChange={(e) => updateEmail(index, e.target.value)}
                    placeholder="email@company.com"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#679632] focus:border-transparent"
                  />
                  <button type="button" onClick={() => removeEmail(index)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" title="Remove">
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject line</label>
            <input
              type="text"
              value={subjectPrefix}
              onChange={(e) => setSubjectPrefix(e.target.value)}
              placeholder="Follow-up - {{company_name}}"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#679632] focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">{'Use {{company_name}} for company name.'}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up 1 template</label>
            <textarea
              value={template1}
              onChange={(e) => setTemplate1(e.target.value)}
              rows={4}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#679632] focus:border-transparent"
              placeholder="Use {{first_name}} and {{company_name}}"
            />
            <button
              type="button"
              onClick={() => sendFollowUp(1)}
              disabled={sending !== null}
              className="mt-2 w-full py-2 bg-[#679632] text-white rounded-lg hover:bg-[#587f2b] disabled:opacity-50 text-sm font-medium"
            >
              {sending === 1 ? 'Sending...' : 'Send Follow-up 1'}
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up 2 template</label>
            <textarea
              value={template2}
              onChange={(e) => setTemplate2(e.target.value)}
              rows={4}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#679632] focus:border-transparent"
              placeholder="Use {{first_name}} and {{company_name}}"
            />
            <button
              type="button"
              onClick={() => sendFollowUp(2)}
              disabled={sending !== null}
              className="mt-2 w-full py-2 bg-[#679632] text-white rounded-lg hover:bg-[#587f2b] disabled:opacity-50 text-sm font-medium"
            >
              {sending === 2 ? 'Sending...' : 'Send Follow-up 2'}
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up 3 template</label>
            <textarea
              value={template3}
              onChange={(e) => setTemplate3(e.target.value)}
              rows={4}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#679632] focus:border-transparent"
              placeholder="Use {{first_name}} and {{company_name}}"
            />
            <button
              type="button"
              onClick={() => sendFollowUp(3)}
              disabled={sending !== null}
              className="mt-2 w-full py-2 bg-[#679632] text-white rounded-lg hover:bg-[#587f2b] disabled:opacity-50 text-sm font-medium"
            >
              {sending === 3 ? 'Sending...' : 'Send Follow-up 3'}
            </button>
          </div>
        </div>

        {message.text && (
          <div className={`flex-shrink-0 p-3 text-sm ${message.type === 'error' ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'}`}>
            {message.text}
          </div>
        )}
      </div>
    </div>
  );
};

export default FollowUpEmailsPanel;
